import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIdCard,
  FaEdit,
  FaArrowLeft,
  FaTrash,
  FaDownload
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const StaffCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const StaffHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const StaffAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
`;

const StaffName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const StaffRole = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const StaffContent = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #F68B21;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-weight: 600;
  color: #141340;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'active':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'inactive':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      case 'pending':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const HRStaffDetail: React.FC = () => {
  // Mock staff data
  const staffData = {
    id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+255 123 456 789',
    address: 'Dar es Salaam, Tanzania',
    employeeId: 'EMP-001',
    department: 'Engineering',
    position: 'Project Manager',
    status: 'active',
    joinDate: '2023-01-15',
    supervisor: 'Managing Director',
    emergencyContact: '+255 987 654 321',
    emergencyName: 'Jane Doe',
    salary: 'TZS 2,500,000',
    leaveBalance: '15 days',
    lastLogin: '2024-01-15 09:30 AM'
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>
          <IconWrapper icon={FaUser} />
          Staff Details
        </PageTitle>
        <ActionButtons>
          <Button variant="secondary" as={Link} to="/hr/staff">
            <IconWrapper icon={FaArrowLeft} />
            Back to List
          </Button>
          <Button variant="secondary">
            <IconWrapper icon={FaDownload} />
            Export
          </Button>
          <Button variant="primary" as={Link} to={`/hr/staff/${staffData.id}/edit`}>
            <IconWrapper icon={FaEdit} />
            Edit Staff
          </Button>
        </ActionButtons>
      </PageHeader>

      <StaffCard>
        <StaffHeader>
          <StaffAvatar>
            {staffData.name.charAt(0)}
          </StaffAvatar>
          <StaffName>{staffData.name}</StaffName>
          <StaffRole>{staffData.position}</StaffRole>
          <StatusBadge status={staffData.status}>
            {staffData.status}
          </StatusBadge>
        </StaffHeader>

        <StaffContent>
          <Section>
            <SectionTitle>
              <IconWrapper icon={FaUser} />
              Personal Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaEnvelope} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue>{staffData.email}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaPhone} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Phone Number</InfoLabel>
                  <InfoValue>{staffData.phone}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMapMarkerAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Address</InfoLabel>
                  <InfoValue>{staffData.address}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaIdCard} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Employee ID</InfoLabel>
                  <InfoValue>{staffData.employeeId}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaUser} />
              Employment Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Department</InfoLabel>
                  <InfoValue>{staffData.department}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Position</InfoLabel>
                  <InfoValue>{staffData.position}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Join Date</InfoLabel>
                  <InfoValue>{new Date(staffData.joinDate).toLocaleDateString()}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Supervisor</InfoLabel>
                  <InfoValue>{staffData.supervisor}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaPhone} />
              Emergency Contact
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Emergency Contact Name</InfoLabel>
                  <InfoValue>{staffData.emergencyName}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaPhone} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Emergency Contact Phone</InfoLabel>
                  <InfoValue>{staffData.emergencyContact}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaUser} />
              Additional Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUser} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Salary</InfoLabel>
                  <InfoValue>{staffData.salary}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Leave Balance</InfoLabel>
                  <InfoValue>{staffData.leaveBalance}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Last Login</InfoLabel>
                  <InfoValue>{staffData.lastLogin}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>
        </StaffContent>
      </StaffCard>
    </Container>
  );
};

export default HRStaffDetail; 